import type { CompareSeoContent } from "~/utils/seo-compare-content";
import { seoPageFontClass } from "~/utils/seo-game-content";
import { getBreadcrumbListJsonLd, getFaqPageJsonLd } from "~/utils/seo-meta";
import { seoRouteLabel } from "~/utils/seo-routes";

const fontClass = seoPageFontClass;

interface CompareSeoPageProps {
  content: CompareSeoContent;
}

export function CompareSeoPage({ content }: CompareSeoPageProps) {
  const { slug, headline, subhead, comparisonName, detailedAnalysis, features, valueTable, whereOtherWins, verdict, whyPrizoWins, pricingNote, faq } = content;
  const pagePath = `/compare/${slug}`;

  return (
    <div className="bg-[#ffffff] w-full">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbListJsonLd([
              { name: "Home", path: "/" },
              { name: seoRouteLabel(pagePath) ?? headline, path: pagePath },
            ]),
          ),
        }}
      />
      {faq?.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getFaqPageJsonLd(faq, pagePath)),
          }}
        />
      )}
      {/* Hero */}
      <div className="seo-section">
        <div className="seo-hero-head">
          <h1 className="seo-h1">
            {headline}
          </h1>
          <p className="seo-lead">
            {subhead}
          </p>
        </div>
        <div className="seo-cta-row">
          <a
            href="https://apps.shopify.com/prizo"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-primary"
          >
            Try Prizo Free
          </a>
          <a
            href="https://calendly.com/nadav-prizo/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-secondary"
          >
            Book a Demo
          </a>
        </div>

        {/* Quick verdict callout */}
        {verdict && (
          <div className="max-w-[758px] mx-auto mt-10 rounded-2xl border border-[#696cff]/20 bg-[#F6F6FF] px-6 py-5">
            <p className={`${fontClass} font-semibold text-[#696cff] text-[13px] uppercase tracking-wide mb-2`}>
              The quick verdict
            </p>
            <p className={`${fontClass} text-[#2c2c2c] text-[15px] md:text-[16px] leading-[1.7]`}>
              {verdict}
            </p>
          </div>
        )}
      </div>

      {/* Detailed analysis */}
      {detailedAnalysis && (
        <div className="seo-band-grey">
          <div className="max-w-[720px] mx-auto px-4 md:px-[120px]">
            <div className="seo-body leading-[1.75] space-y-4">
              {detailedAnalysis.split(/\n\n+/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Comparison Table */}
      <div className="seo-band-violet">
        <div className={`${valueTable?.length ? "max-w-[1000px]" : "max-w-[900px]"} mx-auto px-4 md:px-[120px]`}>
          <h2 className="seo-h2">
            {valueTable?.length ? `Prizo vs ${comparisonName}: side by side` : "Feature Comparison"}
          </h2>
          <div className="overflow-x-auto">
            {valueTable?.length ? (
              <table className="w-full border-collapse bg-white rounded-2xl overflow-hidden shadow-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className={`${fontClass} text-left py-4 px-4 font-semibold text-[#2c2c2c] w-[28%]`}>
                      &nbsp;
                    </th>
                    <th className={`${fontClass} text-left py-4 px-4 font-semibold text-[#696cff]`}>
                      Prizo
                    </th>
                    <th className={`${fontClass} text-left py-4 px-4 font-semibold text-[#6a6a6a]`}>
                      {comparisonName}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {valueTable.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100 align-top">
                      <td className={`${fontClass} py-3 px-4 text-[#2c2c2c] text-[14px] md:text-[15px] font-medium`}>
                        {row.label}
                      </td>
                      <td className={`${fontClass} py-3 px-4 text-[14px] md:text-[15px] leading-[1.5] ${row.prizoWins ? "text-[#2c2c2c] font-semibold bg-[#696cff]/[0.04]" : "text-[#2c2c2c]"}`}>
                        {row.prizo}
                      </td>
                      <td className={`${fontClass} py-3 px-4 text-[#6a6a6a] text-[14px] md:text-[15px] leading-[1.5]`}>
                        {row.other}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full border-collapse bg-white rounded-2xl overflow-hidden shadow-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className={`${fontClass} text-left py-4 px-4 font-semibold text-[#2c2c2c]`}>
                      Feature
                    </th>
                    <th className={`${fontClass} text-center py-4 px-4 font-semibold text-[#696cff]`}>
                      Prizo
                    </th>
                    <th className={`${fontClass} text-center py-4 px-4 font-semibold text-[#6a6a6a]`}>
                      {comparisonName}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className={`${fontClass} py-3 px-4 text-[#2c2c2c] text-[14px] md:text-[16px]`}>
                        {row.feature}
                      </td>
                      <td className="text-center py-3 px-4">
                        {row.prizo ? (
                          <span className="text-[#696cff] font-bold">✓</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="text-center py-3 px-4">
                        {row.other ? (
                          <span className="text-[#6a6a6a]">✓</span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Balanced: where the competitor fits best */}
          {whereOtherWins && whereOtherWins.length > 0 && (
            <div className="max-w-[720px] mx-auto mt-12 rounded-2xl border border-gray-200 bg-white px-6 py-6">
              <h3 className={`${fontClass} font-semibold text-[#2c2c2c] text-[18px] md:text-[20px] mb-4`}>
                When {comparisonName} is the better choice
              </h3>
              <ul className="space-y-3">
                {whereOtherWins.map((item, i) => (
                  <li key={i} className="seo-li">
                    <span className="text-[#6a6a6a] mt-1">•</span>
                    <span className="seo-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Why Prizo Wins */}
      <div className="seo-section">
        <h2 className="seo-h2">
          Why Choose Prizo
        </h2>
        <ul className="seo-list">
          {whyPrizoWins.map((item, i) => (
            <li key={i} className="seo-li">
              <span className="text-[#696cff] mt-1">✓</span>
              <span className="seo-body">{item}</span>
            </li>
          ))}
        </ul>
        <p className={`${fontClass} text-[#6a6a6a] text-[14px] md:text-[16px] text-center mt-8`}>
          {pricingNote}
        </p>
      </div>

      {/* FAQ */}
      {faq?.length > 0 && (
        <div className="seo-section">
          <h2 className="seo-h2">
            Frequently Asked Questions
          </h2>
          <div className="seo-faq-list">
            {faq.map((item, i) => (
              <div key={i} className="seo-faq-item">
                <h3 className="seo-h3">
                  {item.question}
                </h3>
                <p className="seo-muted">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="bg-[rgba(105,108,255,0.06)] w-full py-12 md:py-16">
        <div className="seo-wrap text-center">
          <a
            href="https://apps.shopify.com/prizo"
            target="_blank"
            rel="noopener noreferrer"
            className="seo-btn-cta"
          >
            Switch to Prizo — Free Trial
          </a>
        </div>
      </div>
    </div>
  );
}
